import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  InputAdornment,
  Snackbar,
  Alert,
  AlertColor,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Divider,
  Avatar,
  CircularProgress,
  FormControl,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import { createTheme, ThemeProvider, keyframes } from '@mui/material/styles';
import ScopedCssBaseline from '@mui/material/ScopedCssBaseline';
import {
  Cancel,
  Backspace,
  PlayArrow,
  Settings,
  Key,
  KeyOff,
  Devices,
  Visibility,
  VisibilityOff,
  CheckCircle,
  Warning,
  QrCode2,
  BarChart,
  Print,
  TouchApp,
  Person,
  ReportProblem,
  CheckCircleOutline,
  Business,
  Fullscreen,
  FullscreenExit,
  Delete,
  PlaylistAddCheck,
} from '@mui/icons-material';

// --- API SERVICE ---
import api from '../../../services/api';

// --- LOGO IMPORTS ---
import companyLogo1 from '../../../images/app/data-collection/empresa-1-logo.png';
import companyLogo2 from '../../../images/app/data-collection/empresa-2-logo.png';

// --- BARCODE IMPORTS ---
import barcodeCancelar from '../../../images/app/data-collection/barcode-cancelar.png';
import barcodeIniciar from '../../../images/app/data-collection/barcode-iniciar.png';
import barcodeNao from '../../../images/app/data-collection/barcode-nao.png';
import barcodeParcial from '../../../images/app/data-collection/barcode-parcial.png';
import barcodeRefugo from '../../../images/app/data-collection/barcode-refugo.png';
import barcodeSim from '../../../images/app/data-collection/barcode-sim.png';
import barcodeTotal from '../../../images/app/data-collection/barcode-total.png';

// --- QR CODE IMPORTS ---
import qrCodeCancelar from '../../../images/app/data-collection/qr-code-cancelar.png';
import qrCodeIniciar from '../../../images/app/data-collection/qr-code-iniciar.png';
import qrCodeNao from '../../../images/app/data-collection/qr-code-nao.png';
import qrCodeParcial from '../../../images/app/data-collection/qr-code-parcial.png';
import qrCodeRefugo from '../../../images/app/data-collection/qr-code-refugo.png';
import qrCodeSim from '../../../images/app/data-collection/qr-code-sim.png';
import qrCodeTotal from '../../../images/app/data-collection/qr-code-total.png';

// --- VISUAL ANIMATIONS ---
const pulseDarkText = keyframes`
  0% { color: #0f172a; }
  50% { color: #0284c7; }
  100% { color: #0f172a; }
`;

const pulseYellowBorder = keyframes`
  0% { box-shadow: inset 0 0 0 8px #f59e0b, inset 0 0 15px rgba(245, 158, 11, 0.5); }
  50% { box-shadow: inset 0 0 0 18px #eab308, inset 0 0 35px rgba(234, 179, 8, 0.8); }
  100% { box-shadow: inset 0 0 0 8px #f59e0b, inset 0 0 15px rgba(245, 158, 11, 0.5); }
`;

const defaultTheme = createTheme({
  typography: {
    fontFamily: ['"Roboto"', '"Helvetica"', '"Arial"', 'sans-serif'].join(','),
  },
  palette: {
    mode: 'light',
  },
});

const STORAGE_KEYS = {
  COMPANY_ID: 'cfg_empresa_id',
  BRANCH_ID: 'cfg_filial_id',
  COLLECTOR_NAME: 'cfg_nome_coletor',
  CONNECTION_TOKEN: 'cfg_token_conexao',
  READER_TYPE: 'cfg_tipo_leitor',
  DEVICE_ID: 'cfg_device_id',
};

type ReaderType = 'QRCODE' | 'BARCODE';

const COMMAND_IMAGES = {
  QRCODE: {
    iniciar: qrCodeIniciar,
    parcial: qrCodeParcial,
    refugo: qrCodeRefugo,
    total: qrCodeTotal,
    sim: qrCodeSim,
    nao: qrCodeNao,
    cancelar: qrCodeCancelar,
  },
  BARCODE: {
    iniciar: barcodeIniciar,
    parcial: barcodeParcial,
    refugo: barcodeRefugo,
    total: barcodeTotal,
    sim: barcodeSim,
    nao: barcodeNao,
    cancelar: barcodeCancelar,
  },
};

interface Branch {
  id: number;
  nome: string;
  sigla?: string;
}

interface Company {
  id: number;
  nome: string;
  subtitulo: string;
  corTag?: string;
  logo?: string;
  filiais: Branch[];
}

interface ProductionOrderResponse {
  origin: string;
  originDescription: string;
  op: string;
  stage: string;
  stageDescription: string;
  routeSequence: string;
  orderNumber: string;
  customerName: string;
}

interface OperatorResponse {
  operatorCode: string;
  operatorName: string;
  operatorPhoto: string;
}

interface SaveProductionEntryPayload {
  token: string;
  collectorName: string;
  companyId: number;
  branchId: number;
  origin: string;
  productionOrderNumber: number;
  stageCode: number;
  routeSequence: number;
  operatorCode: number;
  producedQuantity: string;
  scrapQuantity: string;
}

interface TokenValidationResponse {
  valid: boolean;
  collectorName?: string;
  companyId?: number;
  branchId?: number;
  message?: string;
}

type EntryType = 'PARCIAL' | 'REFUGO' | 'TOTAL' | null;

interface ProductionData {
  tipo: EntryType;
  operadorCodigo: string;
  operadorNome: string;
  operadorFoto: string;
  qtdProduzida: string;
  qtdRefugo: string;
}

const INITIAL_DATA: ProductionData = {
  tipo: null,
  operadorCodigo: '',
  operadorNome: '',
  operadorFoto: '',
  qtdProduzida: '',
  qtdRefugo: '',
};

enum Step {
  START = 0,
  SELECT_TYPE = 1,
  READ_OP = 2,
  READ_OPERATOR = 3,
  ENTER_SCRAP = 4,
  ENTER_PRODUCED = 5,
  CONFIRMATION = 6,
}

const getDeviceId = (): string => {
  let devId = localStorage.getItem(STORAGE_KEYS.DEVICE_ID);
  if (!devId) {
    devId = `DEV-${Math.random().toString(36).substring(2, 9).toUpperCase()}`;
    localStorage.setItem(STORAGE_KEYS.DEVICE_ID, devId);
  }
  return devId;
};

export default function ApontamentoProducao() {
  const [companyList, setCompanyList] = useState<Company[]>([]);
  const [isLoadingCompanies, setIsLoadingCompanies] = useState<boolean>(true);
  const [isProcessingScan, setIsProcessingScan] = useState<boolean>(false);
  const [isValidatingToken, setIsValidatingToken] = useState<boolean>(false);
  const [isInitialValidating, setIsInitialValidating] = useState<boolean>(true);
  const [isFullscreen, setIsFullscreen] = useState<boolean>(false);

  const [scannedOPs, setScannedOPs] = useState<ProductionOrderResponse[]>([]);

  // Referência para o final da lista de OPs (rolagem automática)
  const listEndRef = useRef<HTMLDivElement>(null);

  const [companyId, setCompanyId] = useState<number>(() => {
    const saved = localStorage.getItem(STORAGE_KEYS.COMPANY_ID);
    return saved ? Number(saved) : 1;
  });

  const [branchId, setBranchId] = useState<number>(() => {
    const saved = localStorage.getItem(STORAGE_KEYS.BRANCH_ID);
    return saved ? Number(saved) : 1;
  });

  const [collectorName, setCollectorName] = useState<string>(() => {
    return localStorage.getItem(STORAGE_KEYS.COLLECTOR_NAME) || 'COLETOR';
  });

  const [connectionToken, setConnectionToken] = useState<string>(() => {
    return localStorage.getItem(STORAGE_KEYS.CONNECTION_TOKEN) || '';
  });

  const [readerType, setReaderType] = useState<ReaderType>(() => {
    const saved = localStorage.getItem(STORAGE_KEYS.READER_TYPE);
    return saved === 'BARCODE' ? 'BARCODE' : 'QRCODE';
  });

  const hasValidToken = Boolean(connectionToken && connectionToken.trim().length > 0);

  const [tempConnectionToken, setTempConnectionToken] = useState<string>(connectionToken);
  const [tempReaderType, setTempReaderType] = useState<ReaderType>(readerType);
  const [showToken, setShowToken] = useState<boolean>(false);

  const [isConfigModalOpen, setIsConfigModalOpen] = useState<boolean>(false);
  const [isFocused, setIsFocused] = useState<boolean>(true);

  const [currentStep, setCurrentStep] = useState<Step>(Step.START);
  const [productionData, setProductionData] = useState<ProductionData>(INITIAL_DATA);

  const [notification, setNotification] = useState<{
    open: boolean;
    type: AlertColor;
    message: string;
  }>({
    open: false,
    type: 'info',
    message: '',
  });

  const showNotification = (message: string, type: AlertColor = 'error') => {
    setNotification({ open: true, message, type });
  };

  const closeNotification = () => {
    setNotification(prev => ({ ...prev, open: false }));
  };

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen().catch(err => {
        console.error('Erro ao ativar tela cheia:', err);
      });
    } else if (document.exitFullscreen) {
      document.exitFullscreen().catch(err => {
        console.error('Erro ao sair da tela cheia:', err);
      });
    }
  };

  // --- ROLAGEM AUTOMÁTICA PARA O ÚLTIMO ITEM DA LISTA DE OPS ---
  useEffect(() => {
    if (currentStep === Step.READ_OP) {
      listEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [scannedOPs, currentStep]);

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(Boolean(document.fullscreenElement));
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, []);

  useEffect(() => {
    const validateStoredTokenOnMount = async () => {
      const savedToken = localStorage.getItem(STORAGE_KEYS.CONNECTION_TOKEN);

      if (!savedToken || !savedToken.trim()) {
        setIsInitialValidating(false);
        return;
      }

      try {
        setIsInitialValidating(true);
        const { data } = await api.get<TokenValidationResponse>('app/data-collection/validate-collector-token', {
          params: {
            token: savedToken.trim(),
            deviceId: getDeviceId(),
          },
        });

        if (data && data.valid) {
          if (data.companyId) {
            setCompanyId(data.companyId);
            localStorage.setItem(STORAGE_KEYS.COMPANY_ID, String(data.companyId));
          }
          if (data.branchId) {
            setBranchId(data.branchId);
            localStorage.setItem(STORAGE_KEYS.BRANCH_ID, String(data.branchId));
          }
          if (data.collectorName) {
            setCollectorName(data.collectorName);
            localStorage.setItem(STORAGE_KEYS.COLLECTOR_NAME, data.collectorName);
          }
        } else {
          setConnectionToken('');
          localStorage.removeItem(STORAGE_KEYS.CONNECTION_TOKEN);
          showNotification(data?.message || 'Token expirado ou em uso em outro aparelho.', 'warning');
        }
      } catch (error: any) {
        console.error('Error validating token on mount:', error);
        setConnectionToken('');
        localStorage.removeItem(STORAGE_KEYS.CONNECTION_TOKEN);
        const msg = error?.response?.data?.message || 'Token de conexão recusado pelo servidor.';
        showNotification(msg, 'warning');
      } finally {
        setIsInitialValidating(false);
      }
    };

    validateStoredTokenOnMount();
  }, []);

  useEffect(() => {
    const fetchCompaniesAndBranches = async () => {
      try {
        setIsLoadingCompanies(true);
        const { data } = await api.get<Company[]>('app/data-collection/get-company');

        if (data && Array.isArray(data) && data.length > 0) {
          const mappedCompanies = data.map(emp => ({
            ...emp,
            corTag: emp.id === 2 ? '#d97706' : '#0284c7',
            logo: emp.id === 2 ? companyLogo2 : companyLogo1,
          }));

          setCompanyList(mappedCompanies);
        }
      } catch (error) {
        console.error('API Error fetching companies:', error);
        showNotification('Falha ao carregar lista de empresas e filiais da API.', 'error');
      } finally {
        setIsLoadingCompanies(false);
      }
    };

    fetchCompaniesAndBranches();
  }, []);

  const currentCompany = companyList.find(e => e.id === companyId) ||
    companyList[0] || {
      id: 1,
      nome: 'Carregando...',
      subtitulo: '',
      logo: companyLogo1,
      filiais: [{ id: 1, nome: 'Filial 01' }],
    };

  const currentBranch = currentCompany.filiais?.find(f => f.id === branchId) ||
    currentCompany.filiais?.[0] || {
      id: 1,
      nome: 'Filial 01',
    };

  const currentImages = COMMAND_IMAGES[readerType];

  const handleOpenConfigModal = () => {
    setTempConnectionToken(connectionToken);
    setTempReaderType(readerType);
    setIsConfigModalOpen(true);
  };

  const handleSaveConfig = async () => {
    const cleanToken = tempConnectionToken.trim();

    if (!cleanToken) {
      showNotification('Informe um Token de Conexão antes de salvar.', 'warning');
      return;
    }

    try {
      setIsValidatingToken(true);

      const { data } = await api.get<TokenValidationResponse>('app/data-collection/validate-collector-token', {
        params: {
          token: cleanToken,
          deviceId: getDeviceId(),
        },
      });

      if (data && data.valid) {
        const finalCompanyId = data.companyId || companyId;
        const finalBranchId = data.branchId || branchId;
        const finalCollectorName = data.collectorName || 'COLETOR';

        localStorage.setItem(STORAGE_KEYS.COMPANY_ID, String(finalCompanyId));
        localStorage.setItem(STORAGE_KEYS.BRANCH_ID, String(finalBranchId));
        localStorage.setItem(STORAGE_KEYS.COLLECTOR_NAME, finalCollectorName);
        localStorage.setItem(STORAGE_KEYS.CONNECTION_TOKEN, cleanToken);
        localStorage.setItem(STORAGE_KEYS.READER_TYPE, tempReaderType);

        setCompanyId(finalCompanyId);
        setBranchId(finalBranchId);
        setCollectorName(finalCollectorName);
        setConnectionToken(cleanToken);
        setReaderType(tempReaderType);

        setIsConfigModalOpen(false);
        showNotification('Token autenticado e vinculado a este aparelho com sucesso!', 'success');
      } else {
        showNotification(data?.message || 'Token de conexão inválido ou inativo.', 'error');
      }
    } catch (error: any) {
      console.error('Error validating token:', error);
      const errorMessage = error?.response?.data?.message || 'Token de conexão inválido ou em uso em outro coletor.';
      showNotification(errorMessage, 'error');
    } finally {
      setIsValidatingToken(false);
    }
  };

  const handlePrintSheet = () => {
    window.print();
  };

  useEffect(() => {
    const handleFocus = () => setIsFocused(true);
    const handleBlur = () => setIsFocused(false);

    window.addEventListener('focus', handleFocus);
    window.addEventListener('blur', handleBlur);

    setIsFocused(document.hasFocus());

    return () => {
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('blur', handleBlur);
    };
  }, []);

  // --- BUSCAR ORDEM DE PRODUÇÃO (VALIDAÇÃO POR ORIGEM / OP) ---
  const fetchProductionOrderDetails = async (rawCode: string) => {
    try {
      setIsProcessingScan(true);
      const { data } = await api.get<ProductionOrderResponse>('app/data-collection/get-production-order', {
        params: {
          code: rawCode,
          companyId,
          branchId,
        },
      });

      // Validação por Origem + OP
      const exists = scannedOPs.some(
        item =>
          String(item.op).trim() === String(data.op).trim() &&
          String(item.origin).trim() === String(data.origin).trim(),
      );

      if (exists) {
        showNotification(`A OP Origem ${data.origin} / OP Nº ${data.op} já foi adicionada ao lote!`, 'warning');
        return;
      }

      setScannedOPs(prev => [...prev, data]);
      showNotification(`OP ${data.origin}/${data.op} adicionada ao lote!`, 'success');
    } catch (error: any) {
      console.error('Error fetching production order:', error);
      showNotification(error?.response?.data?.message || 'Erro ao consultar Ordem de Produção.', 'error');
    } finally {
      setIsProcessingScan(false);
    }
  };

  const handleRemoveOP = (indexToRemove: number) => {
    setScannedOPs(prev => prev.filter((_, idx) => idx !== indexToRemove));
    showNotification('OP removida do lote.', 'info');
  };

  // --- BUSCAR OPERADOR INFORMANDO COMPANY_ID ---
  const fetchOperatorDetails = async (operatorCode: string) => {
    try {
      setIsProcessingScan(true);
      const { data } = await api.get<OperatorResponse>('app/data-collection/get-operator', {
        params: {
          operatorCode,
          companyId, // Envia o código da empresa selecionada no terminal
        },
      });

      setProductionData(prev => ({
        ...prev,
        operadorCodigo: data.operatorCode,
        operadorNome: data.operatorName,
        operadorFoto: data.operatorPhoto,
      }));

      advanceAfterOperator(productionData.tipo);
    } catch (error: any) {
      console.error('Error fetching operator details:', error);
      showNotification(error?.response?.data?.message || 'Erro ao consultar cadastro do Operador.', 'error');
    } finally {
      setIsProcessingScan(false);
    }
  };

  const resetState = () => {
    setIsProcessingScan(false);
    setScannedOPs([]);
    setProductionData(INITIAL_DATA);
    setCurrentStep(Step.START);
  };

  useEffect(() => {
    let buffer = '';
    let timeout: NodeJS.Timeout;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (isConfigModalOpen || !isFocused || isInitialValidating) return;

      if (e.key === 'Escape') {
        const isQuantityStep = currentStep === Step.ENTER_SCRAP || currentStep === Step.ENTER_PRODUCED;
        const currentField = currentStep === Step.ENTER_SCRAP ? 'qtdRefugo' : 'qtdProduzida';
        const currentValue = productionData[currentField];

        if (isQuantityStep && currentValue.length > 0) {
          handleNumpadPress('CLEAR');
          showNotification('Valor numérico limpo.', 'info');
        } else {
          resetState();
          showNotification('Operação cancelada (ESC).', 'info');
        }
        buffer = '';
        return;
      }

      if (isProcessingScan) return;

      const isQuantityStep = currentStep === Step.ENTER_SCRAP || currentStep === Step.ENTER_PRODUCED;

      if (isQuantityStep) {
        if (/^[0-9]$/.test(e.key)) {
          handleNumpadPress(e.key);
        } else if (e.key === ',' || e.key === '.') {
          handleNumpadPress(',');
        } else if (e.key === 'Backspace') {
          handleNumpadPress('BACKSPACE');
        } else if (e.key === 'Delete' || e.key.toLowerCase() === 'c') {
          handleNumpadPress('CLEAR');
        }
      }

      if (e.key === 'Enter') {
        if (buffer.trim().length > 0) {
          processScannedCode(buffer.trim());
          buffer = '';
        } else if (isQuantityStep) {
          handleNumpadPress('ENTER');
        } else if (currentStep === Step.READ_OP && scannedOPs.length > 0) {
          setCurrentStep(Step.READ_OPERATOR);
        }
        return;
      }

      if (e.key.length === 1) {
        buffer += e.key;
        clearTimeout(timeout);
        timeout = setTimeout(() => {
          buffer = '';
        }, 150);
      }
    };

    const handlePaste = (e: ClipboardEvent) => {
      if (isConfigModalOpen || !isFocused || isProcessingScan || isInitialValidating) return;
      const pastedText = e.clipboardData?.getData('text');
      if (pastedText) {
        processScannedCode(pastedText.trim());
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('paste', handlePaste);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('paste', handlePaste);
    };
  }, [currentStep, productionData, scannedOPs, isConfigModalOpen, isFocused, isProcessingScan, isInitialValidating]);

  // --- CONFIRMAÇÃO DE APONTAMENTO SEQUENCIAL (PREVINE DUPLICIDADE DE CHAVE NO ERP) ---
  const confirmEntry = async () => {
    if (scannedOPs.length === 0) {
      showNotification('Nenhuma OP no lote para apontar.', 'error');
      return;
    }

    try {
      setIsProcessingScan(true);

      await scannedOPs.reduce(async (previousPromise, opItem) => {
        await previousPromise;

        const payload: SaveProductionEntryPayload = {
          token: connectionToken,
          collectorName,
          companyId,
          branchId,
          origin: opItem.origin,
          productionOrderNumber: Number(opItem.op),
          stageCode: Number(opItem.stage),
          routeSequence: Number(opItem.routeSequence) || 0,
          operatorCode: Number(productionData.operadorCodigo),
          producedQuantity: productionData.qtdProduzida || '0',
          scrapQuantity: productionData.qtdRefugo || '0',
        };

        return api.post('app/data-collection/post-production-entry', payload);
      }, Promise.resolve());

      showNotification(`${scannedOPs.length} apontamento(s) de produção registrado(s) com sucesso!`, 'success');
      resetState();
    } catch (error: any) {
      console.error('Error confirming production entry:', error);
      showNotification(error?.response?.data?.message || 'Erro ao gravar os apontamentos no ERP.', 'error');
    } finally {
      setIsProcessingScan(false);
    }
  };

  const processScannedCode = (scannedContent: string) => {
    const raw = scannedContent.trim().toUpperCase();

    if (!hasValidToken) {
      showNotification('Cadastre um Token de Conexão válido antes de apontar.', 'warning');
      return;
    }

    if (['CMD_CANCELAR', 'CANCELAR', 'NAO'].includes(raw)) {
      resetState();
      showNotification('Operação cancelada.', 'info');
      return;
    }

    if (isProcessingScan) return;

    switch (currentStep) {
      case Step.START:
        if (raw === 'START' || raw === 'INICIAR') {
          setCurrentStep(Step.SELECT_TYPE);
        }
        break;

      case Step.SELECT_TYPE:
        if (['PARCIAL', 'REFUGO', 'TOTAL'].includes(raw)) {
          selectEntryType(raw as EntryType);
        }
        break;

      case Step.READ_OP: {
        if (['SIM', 'CONFIRMAR', 'AVANCAR'].includes(raw)) {
          if (scannedOPs.length > 0) {
            setCurrentStep(Step.READ_OPERATOR);
          } else {
            showNotification('Escaneie ao menos uma OP antes de avançar.', 'warning');
          }
        } else if (raw.length === 20 && /^\d+$/.test(raw)) {
          fetchProductionOrderDetails(raw);
        } else {
          showNotification('Código de OP inválido. Formato esperado: 20 caracteres numéricos.', 'error');
        }
        break;
      }

      case Step.READ_OPERATOR:
        if (raw.length === 20 || Number(raw) > 2147483647) {
          showNotification('Código escaneado parece ser uma OP. Escaneie o crachá do Operador.', 'warning');
        } else if (/^\d+$/.test(raw)) {
          fetchOperatorDetails(raw);
        } else {
          showNotification('Código do Operador inválido. Deve conter apenas números.', 'error');
        }
        break;

      case Step.ENTER_SCRAP:
        if (/^\d+([.,]\d+)?$/.test(raw)) {
          const valFormatted = raw.replace('.', ',');
          setProductionData(prev => ({ ...prev, qtdRefugo: valFormatted }));
          setCurrentStep(Step.ENTER_PRODUCED);
        } else {
          showNotification('Digite ou escaneie um valor numérico válido para o refugo.', 'error');
        }
        break;

      case Step.ENTER_PRODUCED:
        if (/^\d+([.,]\d+)?$/.test(raw)) {
          const valFormatted = raw.replace('.', ',');
          setProductionData(prev => ({ ...prev, qtdProduzida: valFormatted }));
          setCurrentStep(Step.CONFIRMATION);
        } else {
          showNotification('Digite ou escaneie um valor numérico válido para a quantidade produzida.', 'error');
        }
        break;

      case Step.CONFIRMATION:
        if (raw === 'SIM' || raw === 'CONFIRMAR') {
          confirmEntry();
        } else if (raw === 'NAO') {
          resetState();
          showNotification('Apontamento descartado pelo operador.', 'warning');
        }
        break;

      default:
        break;
    }
  };

  const selectEntryType = (entryType: EntryType) => {
    setProductionData(prev => ({ ...prev, tipo: entryType }));
    setCurrentStep(Step.READ_OP);
  };

  const advanceAfterOperator = (entryType: EntryType) => {
    if (entryType === 'TOTAL') {
      setCurrentStep(Step.CONFIRMATION);
    } else if (entryType === 'REFUGO') {
      setCurrentStep(Step.ENTER_SCRAP);
    } else {
      setCurrentStep(Step.ENTER_PRODUCED);
    }
  };

  const handleNumpadPress = (val: string) => {
    const currentField = currentStep === Step.ENTER_SCRAP ? 'qtdRefugo' : 'qtdProduzida';
    const currentValue = productionData[currentField];

    if (val === 'CLEAR') {
      setProductionData(prev => ({ ...prev, [currentField]: '' }));
    } else if (val === 'BACKSPACE') {
      setProductionData(prev => ({
        ...prev,
        [currentField]: prev[currentField].slice(0, -1),
      }));
    } else if (val === 'ENTER') {
      if (currentStep === Step.ENTER_SCRAP) {
        setCurrentStep(Step.ENTER_PRODUCED);
      } else if (currentStep === Step.ENTER_PRODUCED) {
        setCurrentStep(Step.CONFIRMATION);
      }
    } else if (val === ',') {
      if (!currentValue.includes(',')) {
        setProductionData(prev => ({
          ...prev,
          [currentField]: currentValue === '' ? '0,' : `${currentValue},`,
        }));
      }
    } else {
      setProductionData(prev => ({
        ...prev,
        [currentField]: prev[currentField] + val,
      }));
    }
  };

  const commandListA4 = [
    { titulo: '1. Iniciar Apontamento', codigo: 'INICIAR', img: currentImages.iniciar },
    { titulo: '2. Apontamento PARCIAL', codigo: 'PARCIAL', img: currentImages.parcial },
    { titulo: '3. Apontamento REFUGO', codigo: 'REFUGO', img: currentImages.refugo },
    { titulo: '4. Apontamento TOTAL', codigo: 'TOTAL', img: currentImages.total },
    { titulo: '5. Confirmar: SIM', codigo: 'SIM', img: currentImages.sim },
    { titulo: '6. Confirmar: NÃO', codigo: 'NAO', img: currentImages.nao },
    { titulo: '7. CANCELAR / Limpar', codigo: 'CANCELAR', img: currentImages.cancelar },
  ];

  const isRefugo = currentStep === Step.ENTER_SCRAP;
  const quantityConfig = isRefugo
    ? {
        titulo: 'DIGITE A QUANTIDADE DE REFUGO',
        subtitulo: 'Informe a quantidade de peças defeituosas/descartadas',
        corHeaderBg: '#fef2f2',
        corHeaderTexto: '#991b1b',
        corBordaVisor: '#dc2626',
        corChip: 'error' as const,
        labelChip: 'ENTRADA DE REFUGO',
        icone: <ReportProblem sx={{ fontSize: { xs: 28, sm: 36 }, color: '#dc2626' }} />,
      }
    : {
        titulo: 'DIGITE A QUANTIDADE PRODUZIDA',
        subtitulo: 'Informe a quantidade de peças boas efetivamente produzidas',
        corHeaderBg: '#f0fdf4',
        corHeaderTexto: '#166534',
        corBordaVisor: '#16a34a',
        corChip: 'success' as const,
        labelChip: 'PEÇAS BOAS / PRODUÇÃO',
        icone: <CheckCircleOutline sx={{ fontSize: { xs: 28, sm: 36 }, color: '#16a34a' }} />,
      };

  return (
    <ThemeProvider theme={defaultTheme}>
      <ScopedCssBaseline
        sx={{
          minHeight: '100vh',
          maxHeight: '100vh',
          backgroundColor: '#f1f5f9',
          color: '#0f172a',
          fontFamily: 'Roboto, sans-serif',
          overflowX: 'hidden',
          overflowY: 'hidden',
        }}
      >
        <style>
          {`
            #folha-a4-impressao {
              display: none !important;
            }

            @media print {
              body * {
                visibility: hidden !important;
              }
              #folha-a4-impressao, #folha-a4-impressao * {
                visibility: visible !important;
              }
              #folha-a4-impressao {
                display: block !important;
                position: absolute !important;
                left: 0 !important;
                top: 0 !important;
                width: 100% !important;
                height: 100% !important;
                max-height: 287mm !important;
                background: #ffffff !important;
                color: #000000 !important;
                padding: 6mm 8mm !important;
                box-sizing: border-box !important;
                page-break-after: avoid !important;
                page-break-inside: avoid !important;
              }
              @page {
                size: A4 portrait;
                margin: 0mm;
              }
            }
          `}
        </style>

        {!isFocused && !isConfigModalOpen && (
          <Box
            id="overlay-sem-foco"
            onClick={() => window.focus()}
            sx={{
              position: 'fixed',
              top: 0,
              left: 0,
              width: '100vw',
              height: '100vh',
              bgcolor: 'rgba(15, 23, 42, 0.82)',
              backdropFilter: 'blur(4px)',
              zIndex: 99999,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#fff',
              cursor: 'pointer',
              animation: `${pulseYellowBorder} 1.5s infinite ease-in-out`,
            }}
          >
            <TouchApp sx={{ fontSize: { xs: 80, sm: 100 }, color: '#f59e0b', mb: 2 }} />
            <Typography
              variant="h3"
              fontWeight="bold"
              textAlign="center"
              gutterBottom
              sx={{ textShadow: '0 2px 10px rgba(0,0,0,0.8)', fontSize: { xs: '1.8rem', sm: '3rem' } }}
            >
              TELA DESATIVADA
            </Typography>
            <Typography
              variant="h6"
              color="grey.200"
              textAlign="center"
              sx={{ maxWidth: 650, px: 2, fontSize: { xs: '1rem', sm: '1.25rem' } }}
            >
              A janela perdeu o foco. <b>Toque em qualquer lugar da tela</b> para reativar o leitor de código.
            </Typography>
          </Box>
        )}

        <Container
          maxWidth="lg"
          sx={{
            py: { xs: 1, sm: 1.5 },
            px: { xs: 1, sm: 2 },
            height: '100vh',
            display: 'flex',
            flexDirection: 'column',
            boxSizing: 'border-box',
            overflowX: 'hidden',
          }}
        >
          {/* HEADER */}
          <Paper
            elevation={4}
            sx={{
              p: { xs: 1, sm: 1.5 },
              mb: 1.5,
              backgroundColor: '#1e293b',
              color: '#fff',
              borderRadius: 2,
              flexShrink: 0,
            }}
          >
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Box display="flex" alignItems="center" gap={1.5}>
                <Box
                  sx={{
                    bgcolor: '#fff',
                    p: 0.5,
                    borderRadius: 1.5,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                    height: { xs: 36, sm: 44 },
                    minWidth: { xs: 70, sm: 90 },
                  }}
                >
                  {isLoadingCompanies ? (
                    <CircularProgress size={20} color="primary" />
                  ) : (
                    <img
                      src={currentCompany.logo || companyLogo1}
                      alt={currentCompany.nome}
                      style={{ maxHeight: '100%', maxWidth: '100%', objectFit: 'contain' }}
                    />
                  )}
                </Box>
                <Box>
                  <Typography
                    variant="h6"
                    fontWeight="bold"
                    sx={{ lineHeight: 1.1, fontSize: { xs: '0.95rem', sm: '1.25rem' } }}
                  >
                    {currentCompany.nome}
                  </Typography>
                  <Typography variant="caption" color="grey.400" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                    {currentBranch.nome}
                  </Typography>
                </Box>
              </Box>

              <Box display="flex" alignItems="center" gap={1}>
                <Box textAlign="right" sx={{ display: { xs: 'none', sm: 'block' }, mr: 0.5 }}>
                  <Box display="flex" gap={1} justifyContent="flex-end" mb={0.2}>
                    <Chip
                      icon={<Devices sx={{ color: '#fff !important' }} />}
                      label={collectorName}
                      color="primary"
                      size="small"
                      sx={{ fontWeight: 'bold', height: 24 }}
                    />
                    <Chip
                      icon={
                        readerType === 'QRCODE' ? (
                          <QrCode2 sx={{ color: '#fff !important' }} />
                        ) : (
                          <BarChart sx={{ color: '#fff !important' }} />
                        )
                      }
                      label={readerType === 'QRCODE' ? 'QR Code' : 'Barcode'}
                      color="secondary"
                      size="small"
                      sx={{ fontWeight: 'bold', height: 24 }}
                    />
                  </Box>

                  <Box display="flex" alignItems="center" justifyContent="flex-end" gap={0.5}>
                    {isInitialValidating ? (
                      <Typography variant="caption" color="grey.400">
                        Validando...
                      </Typography>
                    ) : hasValidToken ? (
                      <>
                        <CheckCircle sx={{ fontSize: 13, color: '#22c55e' }} />
                        <Typography variant="caption" color="#22c55e" fontWeight="bold">
                          Token OK
                        </Typography>
                      </>
                    ) : (
                      <>
                        <Warning sx={{ fontSize: 13, color: '#f59e0b' }} />
                        <Typography variant="caption" color="#f59e0b" fontWeight="bold">
                          Sem Token
                        </Typography>
                      </>
                    )}
                  </Box>
                </Box>

                <IconButton
                  onClick={toggleFullscreen}
                  disabled={isProcessingScan}
                  sx={{
                    bgcolor: 'rgba(255,255,255,0.1)',
                    color: '#fff',
                    p: 1,
                    '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' },
                  }}
                  title={isFullscreen ? 'Sair da Tela Cheia' : 'Ativar Tela Cheia'}
                >
                  {isFullscreen ? <FullscreenExit fontSize="small" /> : <Fullscreen fontSize="small" />}
                </IconButton>

                <IconButton
                  onClick={handleOpenConfigModal}
                  disabled={isProcessingScan}
                  sx={{
                    bgcolor: 'rgba(255,255,255,0.1)',
                    color: '#fff',
                    p: 1,
                    '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' },
                  }}
                  title="Configurações do Terminal"
                >
                  <Settings fontSize="small" />
                </IconButton>
              </Box>
            </Box>
          </Paper>

          {/* MAIN WORKFLOW CONTAINER */}
          <Box
            sx={{
              flexGrow: 1,
              minHeight: 0,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              overflowY: 'auto',
              overflowX: 'hidden',
              py: 0.5,
              position: 'relative',
            }}
          >
            {isInitialValidating ? (
              <Box textAlign="center" py={6}>
                <CircularProgress size={60} color="primary" />
                <Typography variant="h6" fontWeight="bold" mt={2} color="textSecondary">
                  Autenticando Terminal no ERP Senior Sapiens...
                </Typography>
              </Box>
            ) : (
              <>
                {/* STEP 0: START */}
                {currentStep === Step.START && (
                  <Box textAlign="center">
                    {hasValidToken ? (
                      <>
                        <Typography
                          variant="h3"
                          gutterBottom
                          fontWeight="bold"
                          sx={{
                            animation: `${pulseDarkText} 2.2s infinite ease-in-out`,
                            fontSize: { xs: '1.8rem', sm: '3rem' },
                          }}
                        >
                          Aguardando Leitura...
                        </Typography>
                        <Typography
                          variant="h6"
                          color="textSecondary"
                          mb={2}
                          sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}
                        >
                          Aproxime a etiqueta no leitor para começar.
                        </Typography>

                        <Box mb={2}>
                          <img
                            src={currentImages.iniciar}
                            alt="Iniciar Apontamento"
                            style={{ height: 100, maxWidth: '100%', objectFit: 'contain' }}
                          />
                        </Box>

                        <Button
                          variant="contained"
                          color="primary"
                          onClick={() => setCurrentStep(Step.SELECT_TYPE)}
                          sx={{
                            py: { xs: 1.8, sm: 2.5 },
                            px: { xs: 4, sm: 6 },
                            fontSize: { xs: '1.2rem', sm: '1.6rem' },
                            borderRadius: 4,
                            textTransform: 'none',
                          }}
                          startIcon={<PlayArrow sx={{ fontSize: { xs: '2rem', sm: '2.5rem' } }} />}
                        >
                          Iniciar Apontamento
                        </Button>
                      </>
                    ) : (
                      <Paper
                        elevation={6}
                        sx={{
                          p: { xs: 2.5, sm: 4 },
                          maxWidth: 600,
                          mx: 'auto',
                          borderRadius: 4,
                          bgcolor: '#fff7ed',
                          border: '2px solid #f97316',
                          textAlign: 'center',
                        }}
                      >
                        <KeyOff sx={{ fontSize: { xs: 60, sm: 80 }, color: '#ea580c', mb: 2 }} />
                        <Typography
                          variant="h4"
                          fontWeight="bold"
                          color="#9a3412"
                          gutterBottom
                          sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}
                        >
                          Terminal Indisponível
                        </Typography>
                        <Typography variant="body1" color="#c2410c" mb={3} sx={{ lineHeight: 1.4 }}>
                          É necessário configurar um <b>Token de Conexão válido</b> do ERP Senior para habilitar o
                          apontamento neste coletor.
                        </Typography>
                        <Button
                          variant="contained"
                          color="warning"
                          onClick={handleOpenConfigModal}
                          size="large"
                          startIcon={<Settings />}
                          sx={{ py: 1.5, px: 4, fontSize: '1.1rem', fontWeight: 'bold', borderRadius: 3 }}
                        >
                          Configurar Token Agora
                        </Button>
                      </Paper>
                    )}
                  </Box>
                )}

                {/* STEP 1: SELECT ENTRY TYPE */}
                {currentStep === Step.SELECT_TYPE && (
                  <Box textAlign="center">
                    <Typography
                      variant="h4"
                      fontWeight="bold"
                      mb={2}
                      sx={{
                        animation: `${pulseDarkText} 2.2s infinite ease-in-out`,
                        fontSize: { xs: '1.5rem', sm: '2.125rem' },
                      }}
                    >
                      Selecione o Tipo de Apontamento
                    </Typography>
                    <Grid
                      container
                      spacing={{ xs: 1.5, sm: 3 }}
                      justifyContent="center"
                      sx={{ p: 1.5, width: '100%', margin: 0 }}
                    >
                      {[
                        {
                          label: 'PARCIAL',
                          color: '#16a34a',
                          img: currentImages.parcial,
                          desc: '',
                        },
                        {
                          label: 'PARCIAL COM REFUGO',
                          color: '#d97706',
                          img: currentImages.refugo,
                          desc: '',
                        },
                      ].map(item => (
                        <Grid item xs={12} sm={4} key={item.label}>
                          <Card
                            elevation={6}
                            onClick={() => selectEntryType(item.label as EntryType)}
                            sx={{
                              cursor: 'pointer',
                              backgroundColor: item.color,
                              color: '#fff',
                              textAlign: 'center',
                              py: { xs: 1.5, sm: 2 },
                              borderRadius: 3,
                              height: '15rem',
                              transition: 'transform 0.2s, box-shadow 0.2s',
                              '&:hover': {
                                transform: 'scale(1.03)',
                                boxShadow: 12,
                              },
                            }}
                          >
                            <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                              <Box
                                sx={{ height: '6rem', display: 'flex', justifyContent: 'center', alignItems: 'center' }}
                              >
                                <Typography
                                  variant="h4"
                                  fontWeight="bold"
                                  mb={0.5}
                                  sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}
                                >
                                  {item.label}
                                </Typography>
                              </Box>
                              <Box
                                sx={{
                                  bgcolor: '#fff',
                                  p: 1,
                                  display: 'inline-block',
                                  borderRadius: 2,
                                }}
                              >
                                <img
                                  src={item.img}
                                  alt={item.label}
                                  style={{ height: 55, maxWidth: '100%', objectFit: 'contain' }}
                                />
                              </Box>
                            </CardContent>
                          </Card>
                        </Grid>
                      ))}
                    </Grid>
                  </Box>
                )}

                {/* STEP 2: READ OP */}
                {currentStep === Step.READ_OP && (
                  <Box textAlign="center" sx={{ maxWidth: 850, mx: 'auto', width: '100%' }}>
                    <Box display="flex" justifyContent="center" alignItems="center" gap={1.5} mb={1}>
                      <Chip
                        label={`Tipo: ${productionData.tipo}`}
                        color="primary"
                        sx={{ fontSize: '1rem', fontWeight: 'bold' }}
                      />
                      <Chip
                        icon={<PlaylistAddCheck />}
                        label={`OPs no Lote: ${scannedOPs.length}`}
                        color={scannedOPs.length > 0 ? 'success' : 'default'}
                        sx={{ fontSize: '1rem', fontWeight: 'bold' }}
                      />
                    </Box>

                    <Typography
                      variant="h4"
                      fontWeight="bold"
                      mb={0.5}
                      sx={{
                        animation: `${pulseDarkText} 2.2s infinite ease-in-out`,
                        fontSize: { xs: '1.4rem', sm: '1.8rem' },
                      }}
                    >
                      {isProcessingScan ? 'Consultando Ordem de Produção...' : 'Escaneie o Código das OPs'}
                    </Typography>

                    <Typography variant="body2" color="textSecondary" mb={2}>
                      Escaneie uma ou mais etiquetas de OP (20 caracteres). Cada par <b>Origem / OP</b> deve ser
                      diferente. Clique em <b>CONFIRMAR</b> ou escaneie <b>SIM</b> para continuar.
                    </Typography>

                    <Grid container spacing={2} alignItems="flex-start">
                      <Grid item xs={12} md={scannedOPs.length > 0 ? 5 : 12}>
                        <Paper
                          elevation={3}
                          sx={{
                            p: 2,
                            borderRadius: 3,
                            bgcolor: '#fff',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            minHeight: scannedOPs.length > 0 ? 220 : 260,
                          }}
                        >
                          {isProcessingScan ? (
                            <CircularProgress size={70} sx={{ color: currentCompany.corTag || '#0284c7', my: 2 }} />
                          ) : readerType === 'QRCODE' ? (
                            <QrCode2
                              sx={{ fontSize: { xs: 80, sm: 110 }, color: currentCompany.corTag || '#0284c7' }}
                            />
                          ) : (
                            <BarChart
                              sx={{ fontSize: { xs: 80, sm: 110 }, color: currentCompany.corTag || '#0284c7' }}
                            />
                          )}

                          {scannedOPs.length > 0 && (
                            <Button
                              fullWidth
                              variant="contained"
                              color="success"
                              size="large"
                              onClick={() => setCurrentStep(Step.READ_OPERATOR)}
                              sx={{ mt: 2, py: 1, fontWeight: 'bold', borderRadius: 2 }}
                            >
                              <Box display="flex" alignItems="center" justifyContent="center" gap={1.5}>
                                <Typography variant="button" fontWeight="bold" sx={{ fontSize: '1.1rem' }}>
                                  CONFIRMAR ({scannedOPs.length})
                                </Typography>
                                <Box
                                  sx={{
                                    bgcolor: '#fff',
                                    p: 0.5,
                                    borderRadius: 1,
                                    display: 'flex',
                                    alignItems: 'center',
                                  }}
                                >
                                  <img
                                    src={currentImages.sim}
                                    alt="SIM"
                                    style={{ height: 32, maxWidth: '100%', objectFit: 'contain' }}
                                  />
                                </Box>
                              </Box>
                            </Button>
                          )}
                        </Paper>
                      </Grid>

                      {scannedOPs.length > 0 && (
                        <Grid item xs={12} md={7}>
                          <Paper
                            elevation={3}
                            sx={{
                              p: 1.5,
                              borderRadius: 3,
                              bgcolor: '#fff',
                              border: '1px solid #cbd5e1',
                              maxHeight: 280,
                              display: 'flex',
                              flexDirection: 'column',
                            }}
                          >
                            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1} px={0.5}>
                              <Typography variant="subtitle2" fontWeight="bold" color="textSecondary">
                                MINI-LISTA DE OPS ESCANEADAS
                              </Typography>
                              <Chip
                                label={`${scannedOPs.length} item(ns)`}
                                size="small"
                                color="primary"
                                sx={{ fontWeight: 'bold' }}
                              />
                            </Box>
                            <Divider />

                            <List
                              dense
                              sx={{
                                overflowY: 'auto',
                                flexGrow: 1,
                                py: 0.5,
                                '& .MuiListItem-root': {
                                  borderRadius: 1.5,
                                  mb: 0.5,
                                  bgcolor: '#f8fafc',
                                  border: '1px solid #e2e8f0',
                                },
                              }}
                            >
                              {scannedOPs.map((opItem, idx) => (
                                <ListItem
                                  key={`${opItem.origin}-${opItem.op}-${opItem.stage}-${idx}`}
                                  secondaryAction={
                                    <IconButton
                                      edge="end"
                                      color="error"
                                      size="small"
                                      onClick={() => handleRemoveOP(idx)}
                                      title="Remover OP do lote"
                                    >
                                      <Delete fontSize="small" />
                                    </IconButton>
                                  }
                                >
                                  <ListItemText
                                    primary={
                                      <Typography variant="body2" fontWeight="bold">
                                        Origem {opItem.origin} / OP #{opItem.op} ({opItem.originDescription})
                                      </Typography>
                                    }
                                    secondary={
                                      <Typography variant="caption" color="textSecondary" display="block">
                                        Estágio:{' '}
                                        <b>
                                          {opItem.stage} - {opItem.stageDescription}
                                        </b>{' '}
                                        | Seq: {opItem.routeSequence || '0'}
                                        {opItem.customerName ? ` | Cliente: ${opItem.customerName}` : ''}
                                      </Typography>
                                    }
                                  />
                                </ListItem>
                              ))}
                              {/* Elemento de referência para rolagem automática ao adicionar OPs */}
                              <div ref={listEndRef} />
                            </List>
                          </Paper>
                        </Grid>
                      )}
                    </Grid>
                  </Box>
                )}

                {/* STEP 3: READ OPERATOR */}
                {currentStep === Step.READ_OPERATOR && (
                  <Box textAlign="center">
                    <Chip
                      icon={<PlaylistAddCheck />}
                      label={`Lote com ${scannedOPs.length} OP(s)`}
                      color="primary"
                      sx={{ fontSize: '1rem', fontWeight: 'bold', mb: 2 }}
                    />
                    <Typography
                      variant="h4"
                      fontWeight="bold"
                      mb={1}
                      sx={{
                        animation: `${pulseDarkText} 2.2s infinite ease-in-out`,
                        fontSize: { xs: '1.5rem', sm: '2.125rem' },
                      }}
                    >
                      {isProcessingScan ? 'Consultando Cadastro do Operador...' : 'Aguardando Crachá do Operador...'}
                    </Typography>
                    <Typography variant="body1" color="textSecondary" mb={3}>
                      Aproxime o crachá do operador no leitor
                    </Typography>
                    <Box display="flex" justifyContent="center">
                      {isProcessingScan ? (
                        <CircularProgress size={100} sx={{ color: '#16a34a', my: 2 }} />
                      ) : readerType === 'QRCODE' ? (
                        <QrCode2 sx={{ fontSize: { xs: 100, sm: 130 }, color: '#16a34a' }} />
                      ) : (
                        <BarChart sx={{ fontSize: { xs: 100, sm: 130 }, color: '#16a34a' }} />
                      )}
                    </Box>
                  </Box>
                )}

                {/* STEP 4 & 5: ENTER QUANTITY */}
                {(currentStep === Step.ENTER_SCRAP || currentStep === Step.ENTER_PRODUCED) && (
                  <Grid container spacing={{ xs: 2, sm: 3 }} alignItems="stretch">
                    <Grid item xs={12} md={6} sx={{ display: 'flex' }}>
                      <Paper
                        elevation={4}
                        sx={{
                          p: { xs: 1.5, sm: 2.5 },
                          borderRadius: 3,
                          flex: 1,
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'space-between',
                          border: `2px solid ${quantityConfig.corBordaVisor}`,
                        }}
                      >
                        <Box>
                          <Box
                            sx={{
                              p: { xs: 1.2, sm: 1.8 },
                              mb: 2,
                              bgcolor: quantityConfig.corHeaderBg,
                              borderRadius: 2,
                              display: 'flex',
                              flexDirection: { xs: 'column', sm: 'row' },
                              alignItems: { xs: 'flex-start', sm: 'center' },
                              justifyContent: 'space-between',
                              gap: 1,
                            }}
                          >
                            <Box display="flex" alignItems="center" gap={1.5}>
                              {quantityConfig.icone}
                              <Box>
                                <Typography
                                  variant="h5"
                                  fontWeight="bold"
                                  color={quantityConfig.corHeaderTexto}
                                  sx={{ fontSize: { xs: '1.1rem', sm: '1.3rem', md: '1.5rem' }, lineHeight: 1.2 }}
                                >
                                  {quantityConfig.titulo}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  color={quantityConfig.corHeaderTexto}
                                  sx={{ opacity: 0.9, display: 'block', mt: 0.3 }}
                                >
                                  {quantityConfig.subtitulo}
                                </Typography>
                              </Box>
                            </Box>
                            <Chip
                              label={quantityConfig.labelChip}
                              color={quantityConfig.corChip}
                              size="small"
                              sx={{ fontWeight: 'bold', alignSelf: { xs: 'flex-start', sm: 'center' } }}
                            />
                          </Box>

                          <Typography
                            variant="h2"
                            fontWeight="bold"
                            sx={{
                              bgcolor: '#f1f5f9',
                              p: 1.5,
                              borderRadius: 2,
                              textAlign: 'right',
                              minHeight: '75px',
                              color: '#0f172a',
                              border: `3px solid ${quantityConfig.corBordaVisor}`,
                              fontSize: { xs: '2.5rem', sm: '3.75rem' },
                            }}
                          >
                            {(currentStep === Step.ENTER_SCRAP
                              ? productionData.qtdRefugo
                              : productionData.qtdProduzida) || '0'}
                          </Typography>
                        </Box>

                        <Box mt={2} pt={1.5} sx={{ borderTop: '1px solid #e2e8f0' }}>
                          <Box display="flex" alignItems="center" gap={1.5} mb={1}>
                            <Avatar
                              src={productionData.operadorFoto}
                              sx={{ width: 36, height: 36, bgcolor: '#0284c7' }}
                            >
                              <Person fontSize="small" />
                            </Avatar>
                            <Box>
                              <Typography
                                variant="caption"
                                color="textSecondary"
                                display="block"
                                sx={{ lineHeight: 1 }}
                              >
                                Operador Responsável
                              </Typography>
                              <Typography variant="body2" fontWeight="bold">
                                {productionData.operadorNome} ({productionData.operadorCodigo})
                              </Typography>
                            </Box>
                          </Box>

                          <Box sx={{ mt: 1, bgcolor: '#f8fafc', p: 1, borderRadius: 1.5, border: '1px solid #cbd5e1' }}>
                            <Typography variant="caption" color="textSecondary" fontWeight="bold" display="block">
                              OPs NO APONTAMENTO ({scannedOPs.length})
                            </Typography>
                            <Typography variant="body2" fontWeight="bold" color="primary">
                              {scannedOPs.map(o => `${o.origin}/${o.op}`).join(', ')}
                            </Typography>
                          </Box>
                        </Box>
                      </Paper>
                    </Grid>

                    {/* KEYPAD */}
                    <Grid item xs={12} md={6} sx={{ display: 'flex' }}>
                      <Paper
                        elevation={6}
                        sx={{
                          p: { xs: 1.5, sm: 2 },
                          borderRadius: 3,
                          bgcolor: '#e2e8f0',
                          flex: 1,
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'center',
                        }}
                      >
                        <Grid container spacing={{ xs: 1, sm: 1.2 }}>
                          {['7', '8', '9', '4', '5', '6', '1', '2', '3'].map(num => (
                            <Grid item xs={4} key={num}>
                              <Button
                                fullWidth
                                variant="contained"
                                onClick={() => handleNumpadPress(num)}
                                sx={{
                                  height: { xs: 65, sm: 80, md: 85 },
                                  fontSize: { xs: '1.8rem', sm: '2.2rem' },
                                  fontWeight: 'bold',
                                  bgcolor: '#fff',
                                  color: '#000',
                                  '&:hover': { bgcolor: '#f1f5f9' },
                                }}
                              >
                                {num}
                              </Button>
                            </Grid>
                          ))}

                          <Grid item xs={4}>
                            <Button
                              fullWidth
                              variant="contained"
                              onClick={() => handleNumpadPress('0')}
                              sx={{
                                height: { xs: 65, sm: 80, md: 85 },
                                fontSize: { xs: '1.8rem', sm: '2.2rem' },
                                fontWeight: 'bold',
                                bgcolor: '#fff',
                                color: '#000',
                                '&:hover': { bgcolor: '#f1f5f9' },
                              }}
                            >
                              0
                            </Button>
                          </Grid>
                          <Grid item xs={4}>
                            <Button
                              fullWidth
                              variant="contained"
                              onClick={() => handleNumpadPress(',')}
                              sx={{
                                height: { xs: 65, sm: 80, md: 85 },
                                fontSize: { xs: '1.8rem', sm: '2.2rem' },
                                fontWeight: 'bold',
                                bgcolor: '#fff',
                                color: '#000',
                                '&:hover': { bgcolor: '#f1f5f9' },
                              }}
                            >
                              ,
                            </Button>
                          </Grid>
                          <Grid item xs={4}>
                            <Button
                              fullWidth
                              variant="contained"
                              color="warning"
                              onClick={() => handleNumpadPress('BACKSPACE')}
                              sx={{
                                height: { xs: 65, sm: 80, md: 85 },
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                              }}
                            >
                              <Backspace sx={{ fontSize: { xs: '1.8rem', sm: '2.2rem' } }} />
                            </Button>
                          </Grid>

                          <Grid item xs={4}>
                            <Button
                              fullWidth
                              variant="contained"
                              color="error"
                              onClick={() => handleNumpadPress('CLEAR')}
                              sx={{
                                height: { xs: 65, sm: 80, md: 85 },
                                fontSize: { xs: '1rem', sm: '1.2rem' },
                                fontWeight: 'bold',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                              }}
                            >
                              LIMPAR
                            </Button>
                          </Grid>
                          <Grid item xs={8}>
                            <Button
                              fullWidth
                              variant="contained"
                              color="success"
                              onClick={() => handleNumpadPress('ENTER')}
                              sx={{
                                height: { xs: 65, sm: 80, md: 85 },
                                fontSize: { xs: '1.1rem', sm: '1.3rem' },
                                fontWeight: 'bold',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                              }}
                            >
                              CONFIRMAR (ENTER)
                            </Button>
                          </Grid>
                        </Grid>
                      </Paper>
                    </Grid>
                  </Grid>
                )}

                {/* STEP 6: CONFIRMATION */}
                {currentStep === Step.CONFIRMATION && (
                  <Box textAlign="center">
                    <Typography
                      variant="h4"
                      fontWeight="bold"
                      mb={1.5}
                      sx={{
                        animation: `${pulseDarkText} 2.2s infinite ease-in-out`,
                        fontSize: { xs: '1.4rem', sm: '2.125rem' },
                      }}
                    >
                      {isProcessingScan ? 'Gravando Apontamentos...' : 'Confirma os dados do Lote de Apontamento?'}
                    </Typography>

                    <Paper
                      elevation={3}
                      sx={{ p: { xs: 1.5, sm: 2 }, mb: 2, textAlign: 'left', borderRadius: 2.5, width: '100%' }}
                    >
                      <Grid container spacing={1.5}>
                        <Grid
                          item
                          xs={12}
                          display="flex"
                          alignItems="center"
                          gap={1.5}
                          sx={{ bgcolor: '#f8fafc', p: 1, borderRadius: 1.5 }}
                        >
                          <Avatar
                            src={productionData.operadorFoto}
                            sx={{ width: 42, height: 42, bgcolor: '#0284c7', fontSize: '1.2rem', fontWeight: 'bold' }}
                          >
                            {productionData.operadorNome ? productionData.operadorNome.charAt(0) : <Person />}
                          </Avatar>
                          <Box display="flex" justifyContent="space-between" alignItems="center" flexGrow={1}>
                            <Box>
                              <Typography
                                variant="caption"
                                color="textSecondary"
                                fontWeight="bold"
                                display="block"
                                sx={{ lineHeight: 1 }}
                              >
                                OPERADOR RESPONSÁVEL
                              </Typography>
                              <Typography variant="subtitle1" fontWeight="bold" color="#0f172a">
                                {productionData.operadorNome}{' '}
                                <Chip
                                  label={`Cód: ${productionData.operadorCodigo}`}
                                  size="small"
                                  color="primary"
                                  sx={{ ml: 0.5, fontWeight: 'bold', height: 20 }}
                                />
                              </Typography>
                            </Box>
                            <Chip
                              icon={<PlaylistAddCheck />}
                              label={`Lote com ${scannedOPs.length} OP(s)`}
                              color="secondary"
                              sx={{ fontWeight: 'bold' }}
                            />
                          </Box>
                        </Grid>

                        <Grid item xs={12} sm={6}>
                          <Typography variant="caption" color="textSecondary" fontWeight="bold" display="block">
                            EMPRESA / FILIAL
                          </Typography>
                          <Typography variant="body1" fontWeight="bold">
                            {currentCompany.nome} - {currentBranch.nome}
                          </Typography>
                        </Grid>

                        <Grid item xs={12} sm={6}>
                          <Typography variant="caption" color="textSecondary" fontWeight="bold" display="block">
                            TIPO DE APONTAMENTO
                          </Typography>
                          <Typography variant="body1" fontWeight="bold" color="primary">
                            {productionData.tipo}
                          </Typography>
                        </Grid>

                        <Grid item xs={12}>
                          <Typography
                            variant="caption"
                            color="textSecondary"
                            fontWeight="bold"
                            display="block"
                            mb={0.5}
                          >
                            ORDENS DE PRODUÇÃO NO LOTE ({scannedOPs.length})
                          </Typography>
                          <Box
                            sx={{
                              maxHeight: 120,
                              overflowY: 'auto',
                              bgcolor: '#f8fafc',
                              p: 1,
                              borderRadius: 1.5,
                              border: '1px solid #cbd5e1',
                            }}
                          >
                            <Grid container spacing={1}>
                              {scannedOPs.map((o, i) => (
                                <Grid item xs={12} sm={6} key={`${o.origin}-${o.op}-${i}`}>
                                  <Typography variant="caption" display="block">
                                    • Origem <b>{o.origin}</b> / OP <b>#{o.op}</b> - Estágio {o.stage} (Seq:{' '}
                                    {o.routeSequence || '0'})
                                  </Typography>
                                </Grid>
                              ))}
                            </Grid>
                          </Box>
                        </Grid>

                        {productionData.tipo !== 'TOTAL' && (
                          <>
                            <Grid item xs={12} sm={6}>
                              <Paper
                                elevation={0}
                                sx={{ p: 1, bgcolor: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 1.5 }}
                              >
                                <Typography variant="caption" color="textSecondary" fontWeight="bold">
                                  QTD PRODUZIDA (POR OP)
                                </Typography>
                                <Typography variant="h4" fontWeight="bold" color="success.main">
                                  {productionData.qtdProduzida || '0'}
                                </Typography>
                              </Paper>
                            </Grid>
                            {productionData.tipo === 'REFUGO' && (
                              <Grid item xs={12} sm={6}>
                                <Paper
                                  elevation={0}
                                  sx={{ p: 1, bgcolor: '#fef2f2', border: '1px solid #fecaca', borderRadius: 1.5 }}
                                >
                                  <Typography variant="caption" color="textSecondary" fontWeight="bold">
                                    QTD REFUGO (POR OP)
                                  </Typography>
                                  <Typography variant="h4" fontWeight="bold" color="error.main">
                                    {productionData.qtdRefugo || '0'}
                                  </Typography>
                                </Paper>
                              </Grid>
                            )}
                          </>
                        )}
                      </Grid>
                    </Paper>

                    <Grid container spacing={{ xs: 1.5, sm: 2.5 }}>
                      <Grid item xs={6}>
                        <Button
                          fullWidth
                          variant="contained"
                          color="error"
                          disabled={isProcessingScan}
                          onClick={() => {
                            resetState();
                            showNotification('Apontamento descartado pelo operador.', 'warning');
                          }}
                          sx={{
                            py: { xs: 1.5, sm: 2.5 },
                            borderRadius: 2.5,
                            minHeight: { xs: 110, sm: 140 },
                          }}
                        >
                          <Box display="flex" flexDirection="column" alignItems="center" gap={0.8}>
                            <Typography variant="h3" fontWeight="bold" sx={{ fontSize: { xs: '1.5rem', sm: '3rem' } }}>
                              NÃO
                            </Typography>
                            <Box
                              sx={{ bgcolor: '#fff', p: 0.8, borderRadius: 1.5, display: 'flex', alignItems: 'center' }}
                            >
                              <img
                                src={currentImages.nao}
                                alt="NAO"
                                style={{ height: 42, maxWidth: '100%', objectFit: 'contain' }}
                              />
                            </Box>
                          </Box>
                        </Button>
                      </Grid>

                      <Grid item xs={6}>
                        <Button
                          fullWidth
                          variant="contained"
                          color="success"
                          disabled={isProcessingScan}
                          onClick={confirmEntry}
                          sx={{
                            py: { xs: 1.5, sm: 2.5 },
                            borderRadius: 2.5,
                            minHeight: { xs: 110, sm: 140 },
                          }}
                        >
                          <Box
                            display="flex"
                            flexDirection="column"
                            alignItems="center"
                            justifyContent="center"
                            gap={0.8}
                            sx={{ width: '100%', height: '100%' }}
                          >
                            {isProcessingScan ? (
                              <Box display="flex" flexDirection="column" alignItems="center" gap={1}>
                                <CircularProgress size={44} color="inherit" />
                                <Typography
                                  variant="button"
                                  fontWeight="bold"
                                  sx={{ fontSize: { xs: '0.9rem', sm: '1.1rem' } }}
                                >
                                  ENVIANDO LOTE...
                                </Typography>
                              </Box>
                            ) : (
                              <>
                                <Typography
                                  variant="h3"
                                  fontWeight="bold"
                                  sx={{ fontSize: { xs: '1.5rem', sm: '3rem' } }}
                                >
                                  SIM
                                </Typography>
                                <Box
                                  sx={{
                                    bgcolor: '#fff',
                                    p: 0.8,
                                    borderRadius: 1.5,
                                    display: 'flex',
                                    alignItems: 'center',
                                  }}
                                >
                                  <img
                                    src={currentImages.sim}
                                    alt="SIM"
                                    style={{ height: 42, maxWidth: '100%', objectFit: 'contain' }}
                                  />
                                </Box>
                              </>
                            )}
                          </Box>
                        </Button>
                      </Grid>
                    </Grid>
                  </Box>
                )}
              </>
            )}
          </Box>

          {/* FOOTER */}
          {currentStep !== Step.START && !isInitialValidating && (
            <Box mt={1.5} sx={{ flexShrink: 0 }}>
              <Paper
                elevation={4}
                onClick={() => {
                  resetState();
                  showNotification('Operação cancelada.', 'info');
                }}
                sx={{
                  p: 1.5,
                  bgcolor: '#ef4444',
                  color: '#fff',
                  borderRadius: 2.5,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  transition: 'transform 0.15s, background-color 0.15s',
                  '&:hover': { bgcolor: '#dc2626', transform: 'scale(1.005)' },
                }}
              >
                <Box display="flex" alignItems="center" gap={1.5}>
                  <Cancel sx={{ fontSize: { xs: 28, sm: 36 } }} />
                  <Box>
                    <Typography
                      variant="h6"
                      fontWeight="bold"
                      sx={{ lineHeight: 1.1, fontSize: { xs: '0.95rem', sm: '1.25rem' } }}
                    >
                      CANCELAR OPERAÇÃO (ESC)
                    </Typography>
                    <Typography variant="caption" sx={{ opacity: 0.9, display: { xs: 'none', sm: 'block' } }}>
                      Clique aqui ou escaneie o código ao lado para reiniciar a tela
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ bgcolor: '#fff', p: 0.5, borderRadius: 1.5, display: 'flex', alignItems: 'center' }}>
                  <img
                    src={currentImages.cancelar}
                    alt="CANCELAR"
                    style={{ height: 35, maxWidth: '100%', objectFit: 'contain' }}
                  />
                </Box>
              </Paper>
            </Box>
          )}

          <Snackbar
            open={notification.open}
            autoHideDuration={4000}
            onClose={closeNotification}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            sx={{ mb: 2 }}
          >
            <Alert
              onClose={closeNotification}
              severity={notification.type}
              variant="filled"
              sx={{
                width: '100%',
                fontSize: { xs: '1rem', sm: '1.2rem' },
                fontWeight: 'bold',
                alignItems: 'center',
                borderRadius: 2.5,
                boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
              }}
            >
              {notification.message}
            </Alert>
          </Snackbar>

          {/* MODAL DE CONFIGURAÇÕES */}
          <Dialog
            open={isConfigModalOpen}
            onClose={() => !isValidatingToken && setIsConfigModalOpen(false)}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle sx={{ fontWeight: 'bold', fontSize: '1.4rem' }}>Configurações do Coletor</DialogTitle>
            <DialogContent dividers>
              <Box display="flex" flexDirection="column" gap={2.5} pt={1}>
                {hasValidToken && (
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2,
                      bgcolor: '#f0f9ff',
                      border: '1px solid #bae6fd',
                      borderRadius: 2,
                    }}
                  >
                    <Typography variant="caption" color="textSecondary" fontWeight="bold" display="block" mb={1}>
                      DADOS VINCULADOS AO TOKEN
                    </Typography>
                    <Grid container spacing={1.5}>
                      <Grid item xs={12} sm={6}>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Devices color="primary" fontSize="small" />
                          <Box>
                            <Typography variant="caption" color="textSecondary">
                              Nome do Coletor
                            </Typography>
                            <Typography variant="body2" fontWeight="bold">
                              {collectorName}
                            </Typography>
                          </Box>
                        </Box>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Business color="primary" fontSize="small" />
                          <Box>
                            <Typography variant="caption" color="textSecondary">
                              Empresa / Filial
                            </Typography>
                            <Typography variant="body2" fontWeight="bold">
                              {currentCompany.nome} - {currentBranch.nome}
                            </Typography>
                          </Box>
                        </Box>
                      </Grid>
                    </Grid>
                  </Paper>
                )}

                <TextField
                  fullWidth
                  label="Token de Conexão (API ERP Senior)"
                  type={showToken ? 'text' : 'password'}
                  variant="outlined"
                  value={tempConnectionToken}
                  onChange={e => setTempConnectionToken(e.target.value)}
                  disabled={isValidatingToken}
                  placeholder="Cole o token de autenticação aqui..."
                  helperText="Obrigatório. Define a empresa, filial e nome do coletor."
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Key color="primary" />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={() => setShowToken(!showToken)} edge="end" disabled={isValidatingToken}>
                          {showToken ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                <FormControl component="fieldset" disabled={isValidatingToken}>
                  <FormLabel component="legend" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                    Tipo de Leitor de Código
                  </FormLabel>
                  <RadioGroup
                    row
                    value={tempReaderType}
                    onChange={e => setTempReaderType(e.target.value as ReaderType)}
                  >
                    <FormControlLabel value="QRCODE" control={<Radio />} label="QR Code (2D)" />
                    <FormControlLabel value="BARCODE" control={<Radio />} label="Código de Barras (1D)" />
                  </RadioGroup>
                </FormControl>

                <Paper
                  elevation={2}
                  sx={{
                    p: 1.5,
                    bgcolor: '#f8fafc',
                    border: '1px solid #cbd5e1',
                    borderRadius: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                  }}
                >
                  <Box>
                    <Typography variant="subtitle2" fontWeight="bold">
                      Folha de Comandos (A4)
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      Imprima os códigos para colar na bancada
                    </Typography>
                  </Box>
                  <Button
                    variant="contained"
                    color="secondary"
                    onClick={handlePrintSheet}
                    startIcon={<Print />}
                    sx={{ fontWeight: 'bold' }}
                    disabled={isValidatingToken}
                  >
                    IMPRIMIR (A4)
                  </Button>
                </Paper>
              </Box>
            </DialogContent>
            <DialogActions sx={{ p: 2, gap: 1 }}>
              <Button
                variant="outlined"
                color="inherit"
                onClick={() => setIsConfigModalOpen(false)}
                disabled={isValidatingToken}
                sx={{ py: 1.2, fontWeight: 'bold' }}
              >
                CANCELAR
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={handleSaveConfig}
                disabled={isValidatingToken || !tempConnectionToken.trim()}
                startIcon={isValidatingToken ? <CircularProgress size={18} color="inherit" /> : null}
                sx={{ py: 1.2, fontWeight: 'bold', px: 3 }}
              >
                {isValidatingToken ? 'VALIDANDO TOKEN...' : 'SALVAR E AUTENTICAR'}
              </Button>
            </DialogActions>
          </Dialog>

          {/* PRINT SHEET A4 */}
          <div id="folha-a4-impressao">
            <Box textAlign="center" mb={1}>
              <Box display="flex" alignItems="center" justifyContent="center" gap={2} mb={0.5}>
                <img
                  src={currentCompany.logo || companyLogo1}
                  alt={currentCompany.nome}
                  style={{ height: 35, objectFit: 'contain' }}
                />
                <Typography variant="h5" fontWeight="bold">
                  {currentCompany.nome} - {currentBranch.nome}
                </Typography>
              </Box>
              <Typography variant="subtitle1" fontWeight="bold">
                FOLHA DE COMANDOS - COLETOR DE PRODUÇÃO (
                {readerType === 'QRCODE' ? 'QR CODE 2D' : 'CÓDIGO DE BARRAS 1D'})
              </Typography>
              <Typography variant="caption" color="textSecondary">
                Instruções: Escaneie o código correspondente para realizar a ação sem tocar na tela.
              </Typography>
              <Divider sx={{ my: 1, borderBottomWidth: 2, borderColor: '#000' }} />
            </Box>

            <Grid container spacing={1.5}>
              {commandListA4.map(cmd => (
                <Grid item xs={6} key={cmd.codigo}>
                  <Card
                    variant="outlined"
                    sx={{
                      p: 1,
                      textAlign: 'center',
                      borderColor: '#000',
                      borderWidth: 2,
                      borderRadius: 2,
                    }}
                  >
                    <Typography variant="subtitle1" fontWeight="bold" mb={0.5}>
                      {cmd.titulo}
                    </Typography>
                    <Box
                      sx={{
                        bgcolor: '#fff',
                        p: 0.5,
                        display: 'inline-block',
                        border: '1px solid #ccc',
                        borderRadius: 1,
                        mb: 0.5,
                      }}
                    >
                      <img src={cmd.img} alt={cmd.codigo} style={{ height: 55, objectFit: 'contain' }} />
                    </Box>
                    <Typography variant="caption" display="block" fontWeight="bold">
                      CÓDIGO: {cmd.codigo}
                    </Typography>
                  </Card>
                </Grid>
              ))}
            </Grid>

            <Box mt={2} pt={1} sx={{ borderTop: '1px solid #ccc' }} display="flex" justifyContent="space-between">
              <Typography variant="caption">Terminal: {collectorName} | ERP Senior</Typography>
              <Typography variant="caption">Gerado em: {new Date().toLocaleDateString('pt-BR')}</Typography>
            </Box>
          </div>
        </Container>
      </ScopedCssBaseline>
    </ThemeProvider>
  );
}
